import { useState, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useAppStore } from '../../stores/app'
import { useBrandConfig, useUpdateBrandConfig } from '../../hooks/useAdminConfig'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import type { BrandConfig } from '../../lib/api'

const AVAILABLE_CHANNELS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'blog', label: 'Blog' },
  { value: 'podcast', label: 'Podcast' },
] as const

const DEFAULT_STATE: BrandConfig = {
  name: '',
  slug: '',
  voice_tone: '',
  content_pillars: [],
  visual_palette: { primary: '#0F172A', accent: '#3B82F6' },
  channels: [],
  guidelines: '',
}

function parseConfig(raw: Partial<BrandConfig>): BrandConfig {
  return {
    name: raw.name ?? DEFAULT_STATE.name,
    slug: raw.slug ?? DEFAULT_STATE.slug,
    voice_tone: raw.voice_tone ?? DEFAULT_STATE.voice_tone,
    content_pillars: Array.isArray(raw.content_pillars)
      ? [...raw.content_pillars]
      : DEFAULT_STATE.content_pillars,
    visual_palette: {
      primary: raw.visual_palette?.primary ?? DEFAULT_STATE.visual_palette.primary,
      accent: raw.visual_palette?.accent ?? DEFAULT_STATE.visual_palette.accent,
    },
    channels: Array.isArray(raw.channels) ? [...raw.channels] : DEFAULT_STATE.channels,
    guidelines: raw.guidelines ?? DEFAULT_STATE.guidelines,
  }
}

export function SettingsBrandSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { data: configRaw, isLoading } = useBrandConfig(organizationId ?? undefined)
  const updateBrandConfig = useUpdateBrandConfig()

  const [state, setState] = useState<BrandConfig>(DEFAULT_STATE)
  const [pillarInput, setPillarInput] = useState('')
  const pillarInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (configRaw) {
      setState(parseConfig(configRaw))
    }
  }, [configRaw])

  const addPillar = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed || state.content_pillars.includes(trimmed)) return
    setState((s) => ({ ...s, content_pillars: [...s.content_pillars, trimmed] }))
    setPillarInput('')
    pillarInputRef.current?.focus()
  }

  const removePillar = (pillar: string) => {
    setState((s) => ({
      ...s,
      content_pillars: s.content_pillars.filter((p) => p !== pillar),
    }))
  }

  const toggleChannel = (channel: string) => {
    setState((s) => ({
      ...s,
      channels: s.channels.includes(channel)
        ? s.channels.filter((c) => c !== channel)
        : [...s.channels, channel],
    }))
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationId) return
    await updateBrandConfig.mutateAsync({
      orgId: organizationId,
      data: state,
    })
  }

  const busy = updateBrandConfig.isPending

  if (isLoading) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-800">Configuracion de Marca</h2>
        <p className="text-sm text-slate-500 mt-1">
          Nombre, tono de voz, pilares de contenido, paleta visual y canales activos.
        </p>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-slate-800">Configuracion de Marca</h2>
      <p className="text-sm text-slate-500 mt-1">
        Nombre, tono de voz, pilares de contenido, paleta visual y canales activos.
      </p>

      <form onSubmit={handleSave} className="mt-6 space-y-8">
        {/* Brand name */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
            Nombre de Marca
          </h3>
          <input
            type="text"
            className="input max-w-sm"
            placeholder="Ej. Acme Corp"
            value={state.name}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            disabled={busy}
          />
        </div>

        {/* Voice & tone */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
            Tono de Voz
          </h3>
          <textarea
            className="input w-full"
            rows={4}
            placeholder="Describe el tono de voz y personalidad de la marca..."
            value={state.voice_tone}
            onChange={(e) => setState((s) => ({ ...s, voice_tone: e.target.value }))}
            disabled={busy}
          />
        </div>

        {/* Content pillars (tag input) */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-2">
            Pilares de Contenido
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Escribe un pilar y presiona Enter o la coma para agregarlo.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {state.content_pillars.map((pillar) => (
              <span
                key={pillar}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium"
              >
                {pillar}
                <button
                  type="button"
                  onClick={() => removePillar(pillar)}
                  disabled={busy}
                  className="ml-1 text-indigo-500 hover:text-indigo-700 disabled:opacity-40"
                  aria-label={`Quitar ${pillar}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <input
            ref={pillarInputRef}
            type="text"
            className="input max-w-sm"
            placeholder="Nuevo pilar..."
            value={pillarInput}
            onChange={(e) => setPillarInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                addPillar(pillarInput)
              }
            }}
            disabled={busy}
          />
        </div>

        {/* Visual palette */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
            Paleta Visual
          </h3>
          <div className="grid gap-6 sm:grid-cols-2 max-w-md">
            <div>
              <label className="label">Color Primario</label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={state.visual_palette.primary}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      visual_palette: { ...s.visual_palette, primary: e.target.value },
                    }))
                  }
                  disabled={busy}
                  className="h-9 w-14 rounded border border-slate-300 cursor-pointer disabled:opacity-40"
                />
                <input
                  type="text"
                  className="input flex-1 font-mono text-sm"
                  value={state.visual_palette.primary}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      visual_palette: { ...s.visual_palette, primary: e.target.value },
                    }))
                  }
                  placeholder="#1E3A5F"
                  disabled={busy}
                />
              </div>
            </div>
            <div>
              <label className="label">Color Acento</label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={state.visual_palette.accent}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      visual_palette: { ...s.visual_palette, accent: e.target.value },
                    }))
                  }
                  disabled={busy}
                  className="h-9 w-14 rounded border border-slate-300 cursor-pointer disabled:opacity-40"
                />
                <input
                  type="text"
                  className="input flex-1 font-mono text-sm"
                  value={state.visual_palette.accent}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      visual_palette: { ...s.visual_palette, accent: e.target.value },
                    }))
                  }
                  placeholder="#00C896"
                  disabled={busy}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Active channels */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
            Canales Activos
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {AVAILABLE_CHANNELS.map(({ value, label }) => (
              <label key={value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.channels.includes(value)}
                  onChange={() => toggleChannel(value)}
                  disabled={busy}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Guidelines */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
          <h3 className="text-sm font-medium text-slate-700 uppercase tracking-wider mb-4">
            Directrices Editoriales
          </h3>
          <textarea
            className="input w-full"
            rows={3}
            placeholder="Reglas y notas editoriales adicionales para los agentes..."
            value={state.guidelines}
            onChange={(e) => setState((s) => ({ ...s, guidelines: e.target.value }))}
            disabled={busy}
          />
        </div>

        <div className="pt-4 border-t border-slate-200">
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </section>
  )
}
